import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const VISIONARIES = [
  {
    name: "Jemmalyn Hewlett",
    role: "Founder & Executive Producer",
    photo: "/assets/about-founder.jpg",
    bio: [
      "Jemmalyn Hewlett is the visionary behind Poolside Cooking. With a passion for storytelling, entertainment, and bringing people together, she transformed an original concept into a growing television and lifestyle brand. As Founder and Executive Producer, she continues to lead the show's creative vision, production, partnerships, and expansion.",
    ],
  },
  {
    name: "Ashley Marie",
    role: "Co-Founder & Host",
    photo: "/assets/team-ashley.jpg",
    bio: [
      "Ashley Marie brings warmth, personality, and authentic conversation to Poolside Cooking. As Co-Founder and Host, she creates an inviting atmosphere where celebrity guests, chefs, and inspiring personalities can share their stories. Beyond the camera, Ashley helps shape the brand's creative direction, partnerships, and continued growth.",
    ],
  },
];

const STORY_PHOTOS = [
  { src: "/assets/hero-original.jpg", alt: "Ashley Marie with Doug E. Fresh on Poolside Cooking" },
  { src: "/assets/guest-7.jpg", alt: "DJ Kool at the Poolside Cooking Café" },
  { src: "/assets/guest-9.jpg", alt: "Celebrity brunch at the Poolside Cooking Café" },
  { src: "/assets/guest-8.jpg", alt: "Guests on set at Poolside Cooking" },
];

const PROGRESSION = ["The Idea", "Poolside Cooking", "Television", "Celebrity Experiences", "Your Channel Network"];

export default function About() {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      {/* HERO — short, text-led water brand moment */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/50 pointer-events-none" aria-hidden="true" />
        <div className="relative container-site py-12 sm:py-14 lg:py-16">
          <Reveal className="max-w-2xl">
            <p className="label-eyebrow mb-5">About Poolside Cooking</p>
            <h1 className="font-hero font-black tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl text-ink">
              Where Food, Entertainment<br className="hidden sm:block" /> &amp; Culture <span className="text-pool">Come Together.</span>
            </h1>
            <p className="mt-6 text-ink/70 leading-relaxed text-sm sm:text-base max-w-xl" data-testid="about-hero-copy">
              Poolside Cooking is more than a cooking show. It's an entertainment experience built around great food,
              authentic conversation, celebrity guests, culture, music, and unforgettable moments.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MEET THE WOMEN BEHIND POOLSIDE */}
      <section className="bg-white py-16 sm:py-20" data-testid="about-visionaries">
        <div className="container-site">
          <Reveal className="max-w-2xl mx-auto text-center mb-12 sm:mb-16">
            <h2 className="font-display font-black tracking-tight leading-[1.0] text-3xl sm:text-4xl lg:text-5xl text-ink">
              Meet the Women Behind <span className="text-pool">Poolside.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base" data-testid="visionaries-sub">
              Two women. One vision. A passion for bringing people together through food, entertainment, and meaningful conversations.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 max-w-5xl mx-auto">
            {VISIONARIES.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.12}>
                <div data-testid={`visionary-${i}`}>
                  <div className="h-[340px] sm:h-[420px] w-full overflow-hidden rounded-3xl">
                    <img src={v.photo} alt={`${v.name}, ${v.role} of Poolside Cooking`} loading="lazy" className="img-zoom h-full w-full object-cover object-top" />
                  </div>
                  <div className="mt-8">
                    <p className="font-display font-black text-xl sm:text-2xl text-ink" data-testid={`visionary-name-${i}`}>
                      {v.name}
                    </p>
                    <p className="mt-2 text-pool font-bold text-[10px] sm:text-[11px] tracking-[0.28em] uppercase" data-testid={`visionary-role-${i}`}>
                      {v.role}
                    </p>
                    <div className="mt-5 space-y-4 text-slate-600 leading-[1.8] text-sm sm:text-[15px]">
                      {v.bio.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-cream py-12 sm:py-16" data-testid="about-story">
        <div className="container-site grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <Reveal>
            <p className="label-eyebrow mb-4">Our Story</p>
            <h2 className="font-display font-black tracking-tight leading-[1.0] text-3xl sm:text-4xl lg:text-5xl text-ink">
              From One Vision to a <span className="text-pool">Multimedia Brand.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base max-w-xl" data-testid="about-story-copy">
              What started with a vision to bring people together through food has grown into a multimedia
              entertainment brand featuring celebrity interviews, chefs, culture, community stories, live experiences,
              and the Poolside Cooking Celebrity Brunch.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-lg lg:ml-auto" data-testid="about-story-collage">
              {STORY_PHOTOS.map((p, i) => (
                <div key={p.src} className={`relative overflow-hidden rounded-xl shadow-md aspect-[4/3] ${i % 2 === 1 ? "translate-y-3" : ""}`}>
                  <img src={p.src} alt={p.alt} loading="lazy" className="img-zoom absolute inset-0 h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FROM A SHOW TO A NETWORK */}
      <section className="water-panel text-white noise-overlay py-12 sm:py-14" data-testid="about-network">
        <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
        <div className="relative container-site max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="label-eyebrow mb-4">The Next Chapter</p>
            <h2 className="font-display font-black tracking-tight leading-[1.0] text-3xl sm:text-4xl lg:text-5xl">
              From a Show<br />to a <span className="text-pool-light">Network.</span>
            </h2>
            <div className="mt-6 space-y-4 text-white/80 leading-relaxed text-sm sm:text-base max-w-2xl mx-auto" data-testid="about-network-copy">
              <p>
                As Poolside Cooking continued to grow, so did the vision. The journey into television and digital
                media helped inspire an even bigger venture—Your Channel Network.
              </p>
              <p>
                Your Channel Network represents the next evolution of that vision, creating more opportunities for
                original programming, independent creators, entertainers, entrepreneurs, and stories that deserve a
                place on screen.
              </p>
              <p>
                Poolside Cooking remains at the heart of that journey—a reminder of how one original idea can grow
                from a show into a larger entertainment platform.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-3" data-testid="about-progression">
              {PROGRESSION.map((step, i) => (
                <div key={step} className="flex items-center gap-3">
                  <span
                    className={`text-[10px] sm:text-xs tracking-[0.18em] uppercase font-bold px-4 py-2 rounded-full border ${
                      i === PROGRESSION.length - 1
                        ? "bg-white text-ink border-white"
                        : "border-white/40 text-white/85"
                    }`}
                  >
                    {step}
                  </span>
                  {i < PROGRESSION.length - 1 && <span className="text-pool-light font-black" aria-hidden="true">→</span>}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-8 font-display font-black tracking-tight text-lg sm:text-xl lg:text-2xl text-white" data-testid="about-network-tagline">
              FROM THE POOLSIDE TO THE SCREEN — <span className="text-pool-light">AND NOW TO A NETWORK.</span>
            </p>
            {settings.ycn_website_link && (
              <a
                href={settings.ycn_website_link}
                target="_blank"
                rel="noreferrer"
                data-testid="about-ycn-cta"
                className="mt-7 inline-flex items-center gap-3 bg-white text-ink hover:bg-pool-light font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
              >
                Explore Your Channel Network <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
